import { IProjectsResponseDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import { Form, FormInstance } from "antd/es";
import * as React from "react";
import { connect } from "react-redux";
import { ProjectService } from "../../../../services/project-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextBold from "../../../ui-kit/typography/TextBold/TextBold";
import "./project-duplicate-modal.scss";

interface IDuplicateProjectFormValues {
  name: string;
}

export interface IProjectDuplicateModalState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

export interface IProjectDuplicateModalProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  projectId: string;
  closeModal: (isReloadRequired: boolean) => void;
  projectToDuplicate: IProjectsResponseDto;
  isRestoreProject?: boolean;
}

const DUPLICATE_PROJECT_FORM_NAME: string = "duplicateProjectForm";

/** What each commit is doing, rather than the generic "Saving". */
const DUPLICATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Duplicating", success: "Duplicated" };
const RESTORE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Restoring", success: "Restored" };

class ProjectDuplicateModal extends React.PureComponent<
  IProjectDuplicateModalProps & IWithContextProp & IWithHistoryProp,
  IProjectDuplicateModalState
> {
  formRef = React.createRef<FormInstance>();

  inputRef = React.createRef<HTMLInputElement>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IProjectDuplicateModalProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      saveStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidMount() {
    // The dialog open animation steals focus, so defer until it settles (folder-modal idiom).
    setTimeout(() => {
      this.inputRef.current?.focus();
    }, 100);
  }

  private getInitialProjectName = (): string => {
    const baseName: string = this.props.projectToDuplicate.name;
    if (this.props.isRestoreProject) {
      return `${baseName.split("_")[0]}Restore`;
    }
    return `${baseName}Copy`;
  };

  onSubmit = async (data: IDuplicateProjectFormValues) => {
    // A second submit while the copy is made or its ✓ is held would make a second copy.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    try {
      const obj = {
        projectIdToDuplicate: this.props.projectId,
        newProjectName: data.name
      };
      const res = await ProjectService.Duplicate(obj);
      if (res && res.status === 200) {
        // The ✓ goes up first; the dialog leaves after it and the list reloads with the new app.
        this.saveStatus.succeed((): void => this.props.closeModal(true));
      } else {
        // Before, a refused copy left the dialog as if nothing had happened; the button says so now.
        this.saveStatus.fail();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.saveStatus.fail();
    }
  };

  /**
   * Cancel, the X and Escape. The parent unmounts this dialog on close, which would dispose the ✓
   * timer and with it the list's reload, so a close during the hold waits for the dialog's own.
   */
  handleClose = (): void => {
    if (this.saveStatus.current === "success") return;
    this.props.closeModal(false);
  };

  render() {
    const { isRestoreProject } = this.props;
    const title: string = isRestoreProject ? "Restore project" : "Duplicate project";

    const footer = (
      <KModalFooter
        onCancel={this.handleClose}
        submitLabel={isRestoreProject ? "Restore" : "Duplicate"}
        submitTestId="duplicateProjectSubmitButton"
        submitForm={DUPLICATE_PROJECT_FORM_NAME}
        status={this.state.saveStatus}
        statusLabels={isRestoreProject ? RESTORE_STATUS_LABELS : DUPLICATE_STATUS_LABELS}
      />
    );

    return (
      <KModal
        showModal={true}
        closeModal={this.handleClose}
        header={title}
        footer={footer}
        width={460}
        isLoading={this.state.saveStatus === "loading"}
      >
        <div className="kuika-duplicate-project kuika-flex-column--gap-l kuika-padding-l">
          <div className="kuika-duplicate-project__header kuika-flex-column--gap-m kuika-align-center">
            <KuikaModalIconWrapper
              iconName={isRestoreProject ? "restore" : "copy-plus"}
              iconType="common"
              intent={isRestoreProject ? "edit" : "create"}
            />
            <div className="kuika-duplicate-project__title kuika-flex-column--gap-xs kuika-align-center">
              <KuikaH5>{title}</KuikaH5>
              <KuikaText className="kuika-duplicate-project__subtitle">
                {isRestoreProject ? "Restore" : "Create a copy of"}{" "}
                <KuikaTextBold className="kuika-duplicate-project__subtitle-name">
                  {this.props.projectToDuplicate.name}
                </KuikaTextBold>
                {isRestoreProject ? " as a new project" : ""}
              </KuikaText>
            </div>
          </div>

          <div className="kuika-duplicate-project__content kuika-flex-column--gap-m">
            <Form
              name={DUPLICATE_PROJECT_FORM_NAME}
              ref={this.formRef}
              onFinish={this.onSubmit}
              initialValues={{ name: this.getInitialProjectName() }}
            >
              <div className="kuika-duplicate-project__form-item">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a project name!" },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9]*$/,
                      message: "Project name must contains A-Z or a-z or 0-9."
                    },
                    { max: 24, message: "Project name must be max 24 characters." }
                  ]}
                >
                  <KuikaInput
                    ref={this.inputRef}
                    data-testid="duplicateProjectNameInput"
                    disabled={this.state.saveStatus === "loading"}
                    placeholder="Enter project name"
                    size="md"
                  />
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>
      </KModal>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const projectDuplicateModal = connect(
  null,
  mapDispatchToProps
)(withContext<IProjectDuplicateModalProps>(withHistory<IProjectDuplicateModalProps>(ProjectDuplicateModal)));
export { projectDuplicateModal as ProjectDuplicateModal };
